import {
  IPosition,
} from '../types';

type TDragCallback = (deltaX: number, deltaY: number) => void;

interface IUseDragOptions {
  onPan: TDragCallback,
}

export function useDrag({
  onPan,
}: IUseDragOptions) {
  const originalCursor = document.body.style.cursor;
  let lastPos: IPosition = {
    x: 0,
    y: 0,
  };
  let requestAnimationFrameId: number | undefined;
  const moveThreshold = 3; // pixels of movement before drag starts

  function handlePan(event: MouseEvent) {
    event.preventDefault();

    if (requestAnimationFrameId !== undefined) {
      cancelAnimationFrame(requestAnimationFrameId);
    }

    requestAnimationFrameId = requestAnimationFrame(() => {
      const deltaX = event.clientX - lastPos.x;
      const deltaY = event.clientY - lastPos.y;
      lastPos = {
        x: event.clientX,
        y: event.clientY,
      };
      onPan(deltaX, deltaY);
    });
  }

  function stop() {
    document.body.style.cursor = originalCursor;
    document.removeEventListener('mousemove', onFirstMove);
    document.removeEventListener('mousemove', handlePan);
    document.removeEventListener('mouseup', stop);
  }

  function onFirstMove(event: MouseEvent) {
    const dx = event.clientX - lastPos.x;
    const dy = event.clientY - lastPos.y;
    const distance = Math.sqrt(dx * dx + dy * dy);

    if (distance > moveThreshold) {
      // User actually moved — start dragging now
      document.removeEventListener('mousemove', onFirstMove);
      document.body.style.cursor = 'grabbing';
      handlePan(event);
      document.addEventListener('mousemove', handlePan);
    }
  }

  function start(event: MouseEvent) {
    // Let inputs behave normally
    if (event.target instanceof HTMLInputElement) {
      return;
    }
    // clicking on the background should always remove the focus of input
    if (document.activeElement && document.activeElement !== document.body && document.activeElement instanceof HTMLInputElement) {
      document.activeElement.blur();
    }

    event.preventDefault();
    lastPos.x = event.clientX;
    lastPos.y = event.clientY;

    document.addEventListener('mousemove', onFirstMove);
    document.addEventListener('mouseup', stop);
  }

  return {
    start,
    stop,
  };
}
